import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Expense {
  id: number
  expense_date: string | null
  user: { id: number; name: string } | null
  expense_type: string | null
  expense_type_ref: { id: number; name: string } | null
  vendor: { id: number; name: string } | null
  amount: number
  paid_to: string | null
  preapproved: boolean
  provider: string | null
  city: string | null
  state: string | null
  phone: string | null
  notes: string | null
  paid_by: string | null
  per_diem_breakfast: number
  per_diem_lunch: number
  per_diem_dinner: number
  total_per_diem: number
  fuel_vehicle: string | null
  fuel_start_odometer: number
  fuel_end_odometer: number
  fuel_mileage: number
  room_number: number | null
  room_occupants: string | null
  stay_start: string | null
  stay_end: string | null
  rental_make: string | null
  rental_model: string | null
  rental_color: string | null
  rental_tag: string | null
  rental_tag_state: string | null
  air_flight1: string | null
  air_flight2: string | null
  air_destination: string | null
  has_receipt: boolean
  receipt_status: string | null
  receipt_url: string | null
  receipt_filename: string | null
  receipt_content_type: string | null
  receipt_image: string | null
  created_at: string | null
  closed_at: string | null
  approval_notes: string | null
  approved_at: string | null
  approved_by: string | null
  paid_at: string | null
  paid_reference: string | null
  billed_at: string | null
}

interface Props {
  case_record: CaseRecord
  expense: Expense
  can_edit?: boolean
  can_approve?: boolean
  can_delete?: boolean
  can_submit?: boolean
}

export default function Show({
  case_record,
  expense,
  can_edit,
  can_approve,
  can_submit,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Expenses", href: `/cases/${case_record.id}/expenses` },
    {
      title: "Details",
      href: `/cases/${case_record.id}/expenses/${expense.id}`,
    },
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDateTime = (date: string | null) => {
    if (!date) return "-"
    return new Date(date).toLocaleString()
  }

  const formatDate = (date: string | null) => {
    return (
      formatDateUtil(date, {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }) || "-"
    )
  }

  const expenseTypeName = (type: string | null) => {
    const types: Record<string, string> = {
      F: "Fuel",
      L: "Lodging",
      P: "Per Diem",
      R: "Rental Car",
      A: "Airfare",
      O: "Other",
    }
    return type ? types[type] || type : "-"
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Expense - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Expense Details</h1>
          <div className="space-x-2">
            {can_submit && (
              <button
                onClick={() =>
                  router.patch(
                    `/cases/${case_record.id}/expenses/${expense.id}/submit`,
                  )
                }
                className="bg-hpi-yellow hover:bg-hpi-yellow/80 rounded px-4 py-2 text-white"
              >
                Submit for Approval
              </button>
            )}
            {can_edit && (
              <Link
                href={`/cases/${case_record.id}/expenses/${expense.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Info */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Basic Information</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Date</dt>
                <dd>{expense.expense_date}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Employee</dt>
                <dd>{expense.user?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Type</dt>
                <dd>{expenseTypeName(expense.expense_type)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Category</dt>
                <dd>{expense.expense_type_ref?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Vendor</dt>
                <dd>{expense.vendor?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Paid By</dt>
                <dd>{expense.paid_by ?? "-"}</dd>
              </div>
              {expense.paid_to && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Paid To</dt>
                  <dd>{expense.paid_to}</dd>
                </div>
              )}
              {expense.preapproved && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Pre-Approved</dt>
                  <dd className="text-hpi-dark-green">Yes</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Amounts */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Amounts</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Amount</dt>
                <dd className="font-semibold">
                  {formatCurrency(expense.amount)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Receipt</dt>
                <dd>
                  {expense.receipt_url ? (
                    <a
                      href={expense.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue inline-flex items-center gap-1 hover:underline"
                    >
                      {expense.receipt_content_type === "application/pdf" ? (
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                      {expense.receipt_filename}
                    </a>
                  ) : expense.has_receipt ? (
                    <div>
                      <span className="text-hpi-dark-green">Yes (legacy)</span>
                      {expense.receipt_image && (
                        <div className="text-hpi-med-gray text-xs">
                          {expense.receipt_image}
                        </div>
                      )}
                    </div>
                  ) : (
                    "No"
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Type-specific fields */}
          {expense.expense_type === "P" && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Per Diem</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Breakfast</dt>
                  <dd>{formatCurrency(expense.per_diem_breakfast)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Lunch</dt>
                  <dd>{formatCurrency(expense.per_diem_lunch)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Dinner</dt>
                  <dd>{formatCurrency(expense.per_diem_dinner)}</dd>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <dt className="font-semibold">Total</dt>
                  <dd className="font-semibold">
                    {formatCurrency(expense.total_per_diem)}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {expense.expense_type === "F" && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Fuel</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Vehicle</dt>
                  <dd>{expense.fuel_vehicle ?? "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Start Odometer</dt>
                  <dd>{expense.fuel_start_odometer.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">End Odometer</dt>
                  <dd>{expense.fuel_end_odometer.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <dt className="font-semibold">Mileage</dt>
                  <dd className="font-semibold">
                    {expense.fuel_mileage.toFixed(2)} mi
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {expense.expense_type === "L" && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Lodging</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Provider</dt>
                  <dd>{expense.provider ?? "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Location</dt>
                  <dd>
                    {expense.city && expense.state
                      ? `${expense.city}, ${expense.state}`
                      : "-"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Room Number</dt>
                  <dd>{expense.room_number ?? "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Check-in</dt>
                  <dd>{formatDateTime(expense.stay_start)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Check-out</dt>
                  <dd>{formatDateTime(expense.stay_end)}</dd>
                </div>
              </dl>
            </div>
          )}

          {expense.expense_type === "R" && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Rental Car</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Vehicle</dt>
                  <dd>
                    {expense.rental_make && expense.rental_model
                      ? `${expense.rental_make} ${expense.rental_model}`
                      : "-"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Color</dt>
                  <dd>{expense.rental_color ?? "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">License Plate</dt>
                  <dd>
                    {expense.rental_tag
                      ? `${expense.rental_tag} (${expense.rental_tag_state})`
                      : "-"}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {expense.expense_type === "A" && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Airfare</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Flight 1</dt>
                  <dd>{expense.air_flight1 ?? "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Flight 2</dt>
                  <dd>{expense.air_flight2 ?? "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Destination</dt>
                  <dd>{expense.air_destination ?? "-"}</dd>
                </div>
              </dl>
            </div>
          )}

          {/* Workflow Status */}
          <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
            <h2 className="mb-4 text-lg font-semibold">Workflow Status</h2>
            <div className="grid gap-4 md:grid-cols-5">
              <div>
                <h3 className="font-medium">Added</h3>
                <div className="text-hpi-med-gray mt-1 text-sm">
                  {formatDate(expense.created_at)}
                </div>
              </div>
              <div>
                <h3 className="font-medium">Submitted</h3>
                {expense.closed_at ? (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    {formatDate(expense.closed_at)}
                  </div>
                ) : (
                  <div className="text-hpi-yellow mt-1 text-sm">
                    Not Submitted
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Approval</h3>
                {expense.approved_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Approved</div>
                    <div className="text-hpi-med-gray">
                      by {expense.approved_by}
                    </div>
                    <div className="text-hpi-med-gray">
                      {formatDate(expense.approved_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-yellow mt-1 text-sm">
                    Pending Approval
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Payment</h3>
                {expense.paid_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Paid</div>
                    {expense.paid_reference && (
                      <div className="text-hpi-med-gray">
                        Ref: {expense.paid_reference}
                      </div>
                    )}
                    <div className="text-hpi-med-gray">
                      {formatDate(expense.paid_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    Not Yet Paid
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Billing</h3>
                {expense.billed_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Billed</div>
                    <div className="text-hpi-med-gray">
                      {formatDate(expense.billed_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    Not Yet Billed
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Approve / Reject Panel */}
          {can_approve && !expense.approved_at && expense.closed_at && (
            <ApproveRejectPanel
              approveUrl={`/cases/${case_record.id}/expenses/${expense.id}/approve`}
              rejectUrl={`/cases/${case_record.id}/expenses/${expense.id}/reject`}
            />
          )}

          {/* Approval History */}
          {expense.approval_notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Approval History</h2>
              <pre className="text-hpi-dark-gray text-sm whitespace-pre-wrap">
                {expense.approval_notes}
              </pre>
            </div>
          )}

          {/* Notes */}
          {expense.notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Notes</h2>
              <p className="text-hpi-dark-gray whitespace-pre-wrap">
                {expense.notes}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={`/cases/${case_record.id}/expenses`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Expenses
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}

function ApproveRejectPanel({
  approveUrl,
  rejectUrl,
}: {
  approveUrl: string
  rejectUrl: string
}) {
  const [notes, setNotes] = useState("")
  const [processing, setProcessing] = useState(false)

  const handleApprove = () => {
    setProcessing(true)
    router.patch(
      approveUrl,
      { notes },
      { onFinish: () => setProcessing(false) },
    )
  }

  const handleReject = () => {
    if (!notes.trim()) {
      alert("Please provide a reason for rejection.")
      return
    }
    setProcessing(true)
    router.patch(rejectUrl, { notes }, { onFinish: () => setProcessing(false) })
  }

  return (
    <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-6 shadow md:col-span-2">
      <h2 className="mb-4 text-lg font-semibold">Review</h2>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional note (required for rejection)"
        rows={3}
        className="border-hpi-light-gray mb-4 w-full rounded border p-2 text-sm"
      />
      <div className="flex gap-3">
        <button
          onClick={handleApprove}
          disabled={processing}
          className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-6 py-2 text-white disabled:opacity-50"
        >
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={processing}
          className="rounded bg-red-600 px-6 py-2 text-white hover:bg-red-700 disabled:opacity-50"
        >
          Reject
        </button>
      </div>
    </div>
  )
}
